import React, { useCallback, useMemo, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import { Alert, Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { Dayjs, toDayJs } from '~/services/TimeService';

import { ConflictResolution, ExistingBlackoutDate, ManualRoom } from '~/types/stopsell';

import { formatDateRange } from '~/utils/stopsellDateUtils';

import StopsellManualRoomDateRow from './StopsellManualRoomDateRow';

interface Props {
  room: ManualRoom;
  roomIndex: number;
  blackoutDates: Array<ExistingBlackoutDate>;
  catalogRoomRates: Array<{ id: string; name: string }>;
  rateResolutions: Record<string, ConflictResolution>;
  onAddDate: (roomIndex: number, start: string, end: string) => void;
  onRemoveRoom: (roomIndex: number) => void;
  onRemoveDate: (roomIndex: number, dateIndex: number) => void;
  onToggleDate: (roomIndex: number, dateIndex: number, selected: boolean) => void;
  onResolve: (roomIndex: number, dateIndex: number, rateId: string, status: ConflictResolution) => void;
  onUndo: (roomIndex: number, dateIndex: number, rateId: string) => void;
}

export default function StopsellManualRoomCard({
  room,
  roomIndex,
  blackoutDates,
  catalogRoomRates,
  rateResolutions,
  onAddDate,
  onRemoveRoom,
  onRemoveDate,
  onToggleDate,
  onResolve,
  onUndo,
}: Props) {
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);

  const handleAddDate = useCallback(() => {
    if (!startDate || !endDate) return;
    if (endDate.isBefore(startDate)) {
      setDateError('End date must be on or after start date');
      return;
    }
    onAddDate(roomIndex, startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'));
    setStartDate(null);
    setEndDate(null);
    setDateError(null);
  }, [startDate, endDate, onAddDate, roomIndex]);

  const handleRemoveRoom = useCallback(() => onRemoveRoom(roomIndex), [onRemoveRoom, roomIndex]);

  const handleRemoveDate = useCallback(
    (dateIndex: number) => onRemoveDate(roomIndex, dateIndex),
    [onRemoveDate, roomIndex],
  );

  const handleToggleDate = useCallback(
    (dateIndex: number, selected: boolean) => onToggleDate(roomIndex, dateIndex, selected),
    [onToggleDate, roomIndex],
  );

  const handleResolve = useCallback(
    (dateIndex: number, rateId: string, status: ConflictResolution) => onResolve(roomIndex, dateIndex, rateId, status),
    [onResolve, roomIndex],
  );

  const handleUndo = useCallback(
    (dateIndex: number, rateId: string) => onUndo(roomIndex, dateIndex, rateId),
    [onUndo, roomIndex],
  );

  const today = useMemo(() => toDayJs(new Date().toISOString().slice(0, 10)), []);
  const futureBlackouts = blackoutDates.filter((d) => !toDayJs(d.end).isBefore(today, 'day'));
  const existingLabel =
    futureBlackouts.length > 0 ? futureBlackouts.map((d) => formatDateRange(d.start, d.end)).join(', ') : null;

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Stack gap={0.5}>
          <Typography fontWeight={500}>{room.catalogRoomName}</Typography>
          {existingLabel && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <EventBusyOutlinedIcon sx={{ fontSize: 13, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                Existing: {existingLabel}
              </Typography>
            </Stack>
          )}
        </Stack>
        <IconButton size="small" onClick={handleRemoveRoom}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Stack direction="row" gap={1} alignItems="center" mb={1}>
        <DatePicker
          label="From"
          value={startDate}
          onChange={(val) => {
            setStartDate(val);
            setDateError(null);
          }}
          slotProps={{ textField: { size: 'small' } }}
        />
        <DatePicker
          label="To"
          value={endDate}
          onChange={(val) => {
            setEndDate(val);
            setDateError(null);
          }}
          slotProps={{ textField: { size: 'small' } }}
        />
        <Button variant="contained" onClick={handleAddDate} disabled={!startDate || !endDate}>
          Add Date
        </Button>
      </Stack>

      {dateError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {dateError}
        </Alert>
      )}

      {room.dates.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No dates added yet
        </Typography>
      ) : (
        <Stack gap={0.5}>
          {room.dates.map((date, dateIndex) => (
            <StopsellManualRoomDateRow
              key={`${date.start}-${date.end}-${dateIndex}`}
              date={date}
              dateIndex={dateIndex}
              catalogRoomRates={catalogRoomRates}
              blackoutDates={blackoutDates}
              rateResolutions={rateResolutions}
              onRemoveDate={handleRemoveDate}
              onToggleDate={handleToggleDate}
              onResolve={handleResolve}
              onUndo={handleUndo}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
}
